/**
 * In-browser loader for a delivered widget zip - the "testers only have the zip
 * and the lobby" path. Everything happens inside the QA panel: the tester picks
 * the zip file, we unpack it in the browser, load the bundle from blob URLs, and
 * translate the widget's asset URLs to in-memory blobs. No host to deploy to, no
 * Service Worker, no tooling from the jackpot-widget project.
 *
 * It presents the SAME surface the remote injector (qa-inject.js) exposes, so
 * JackpotWidgetController drives it exactly the same way - only the source of
 * the bytes differs.
 *
 * ── The asset trick (why no Service Worker) ─────────────────────────────────
 * The widget builds every image URL as `assetsHost + assetsLocation + path` and
 * renders it as an <img src> or, for the Card variant only, an inline
 * background-image. The built CSS has zero url() references. So instead of
 * hosting the files, we point assetsHost at a sentinel path and translate on the
 * fly to blob URLs held in memory:
 *   - <img>: the src setter and setAttribute are patched on
 *     HTMLImageElement.prototype, so the swap happens at assignment time and no
 *     failed request is ever made.
 *   - inline background-image: a MutationObserver scoped to the mounted section
 *     rewrites any style url() that carries the sentinel.
 * Only URLs carrying the sentinel are touched; everything else passes straight
 * through. Both hooks are trivial while inactive and are fully torn down on
 * unmount.
 */

import type { JSZipObject } from 'jszip';
import type { WidgetInjector } from './widgetInjector';

/** Options mount() accepts - mirrors qa-inject.js's public contract. */
export interface ZipMountOptions {
    jackpots?: string[];
    type?: string;
    currency?: string;
    units?: number;
    language?: string;
    apiHost?: string;
    mock?: boolean;
    title?: string;
    className?: string;
}

/** Marks the virtual asset root. assetsHost points here; nothing hits network. */
const SENTINEL = '__egtd_jpw_zip__';
const ASSETS_LOCATION = '/assets/egtd_jp_widgets';
const STATS_PATH = '/api/jackpot/stats';

const SECTION_CLASS = 'egtd-qa-jpw-section';
const STYLE_ID = 'egtd-qa-jpw-style';

const MIME: Record<string, string> = {
    js: 'text/javascript',
    css: 'text/css',
    svg: 'image/svg+xml',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    webp: 'image/webp',
    avif: 'image/avif',
    json: 'application/json',
    woff: 'font/woff',
    woff2: 'font/woff2',
};

const mimeFor = (name: string): string => {
    const ext = name.slice(name.lastIndexOf('.') + 1).toLowerCase();
    return MIME[ext] || 'application/octet-stream';
};

/** The widget factory the bundle registers on window once loaded. */
type WidgetFactory = (opts: { el: HTMLElement; jackpot: string; type: string }) => unknown;

interface GlobalShape {
    EGTD_JackpotWidget?: WidgetFactory;
    EGTD_JackpotWidgetConfig?: Record<string, unknown>;
    fetch: typeof fetch;
}

const g = window as unknown as GlobalShape;

/**
 * Pull the version, bundle filenames and the jackpot/type/language lists
 * straight out of the zip's own qa-inject.js text, so they never drift from
 * what the bundle registers - the same guarantee the remote injector gives.
 */
function parseInjectorConstants(src: string): {
    version: string;
    bundleJs: string;
    bundleCss: string;
    jackpots: string[];
    types: string[];
    languages: string[];
} {
    const str = (name: string): string => {
        const m = src.match(new RegExp(name + "\\s*=\\s*'([^']*)'"));
        return m ? m[1] : '';
    };
    const arr = (name: string): string[] => {
        const m = src.match(new RegExp(name + '\\s*=\\s*(\\[[^\\]]*\\])'));
        if (!m) return [];
        try {
            return JSON.parse(m[1]) as string[];
        } catch {
            return [];
        }
    };
    return {
        version: str('VERSION'),
        bundleJs: str('BUNDLE_JS'),
        bundleCss: str('BUNDLE_CSS'),
        jackpots: arr('JACKPOTS'),
        types: arr('TYPES'),
        languages: arr('LANGUAGES'),
    };
}

/**
 * Build an injector from raw zip bytes. Unpacks in-browser, wires up the asset
 * translation, and returns an object JackpotWidgetController can load()/mount()
 * like the remote one.
 */
export async function createZipInjector(zipBytes: ArrayBuffer): Promise<WidgetInjector> {
    // Kept out of the main bundle: this whole feature is dynamically imported
    // behind the qaPanel env gate, and jszip is only pulled in when a tester
    // actually loads a zip.
    const { default: JSZip } = await import('jszip');
    const zip = await JSZip.loadAsync(zipBytes);

    // Locate the folder holding qa-inject.js (the zip's docs/); everything the
    // injector serves lives under that prefix. Collect paths first, then find -
    // TS narrowing does not track a `let` assigned inside a forEach closure.
    const allPaths: string[] = [];
    zip.forEach((relPath) => {
        allPaths.push(relPath);
    });
    const injectPath = allPaths.find((p) => /(^|\/)qa-inject\.js$/.test(p));
    if (!injectPath) {
        throw new Error('That zip has no qa-inject.js - it was built without the QA harness (docs/). Rebuild with yarn build:all.');
    }
    const prefix = injectPath.replace(/qa-inject\.js$/, '');

    // Read a required entry, failing loudly rather than with a bare assertion.
    const readText = async (path: string): Promise<string> => {
        const entry = zip.file(path);
        if (!entry) throw new Error('The zip is missing ' + path + '.');
        return entry.async('string');
    };

    const injectText = await readText(prefix + 'qa-inject.js');
    const constants = parseInjectorConstants(injectText);
    if (!constants.bundleJs || !constants.jackpots.length) {
        throw new Error('Could not read the bundle info from qa-inject.js - is this a jackpot-widget zip?');
    }

    const bundleJsText = await readText(prefix + 'static/js/' + constants.bundleJs);
    const bundleCssFile = zip.file(prefix + 'static/css/' + constants.bundleCss);
    const bundleCssText = bundleCssFile ? await bundleCssFile.async('string') : '';

    // Every asset -> a blob URL, keyed by its path under docs/ (e.g.
    // "assets/egtd_jp_widgets/bell_link/x.png"). Created up front so the first
    // render never waits on decompression.
    const assetBlobUrls = new Map<string, string>();
    const createdUrls: string[] = [];
    const assetEntries: Array<{ rel: string; file: JSZipObject }> = [];
    zip.forEach((relPath, file) => {
        if (relPath.startsWith(prefix + 'assets/') && !file.dir) {
            assetEntries.push({ rel: relPath.slice(prefix.length), file });
        }
    });
    await Promise.all(
        assetEntries.map(async ({ rel, file }) => {
            const bytes = await file.async('uint8array');
            const url = URL.createObjectURL(new Blob([bytes], { type: mimeFor(rel) }));
            assetBlobUrls.set(rel, url);
            createdUrls.push(url);
        }),
    );

    // ── Asset URL translation ──────────────────────────────────────────────
    // A widget URL looks like `<origin>/__egtd_jpw_zip__/assets/egtd_jp_widgets/...`.
    const marker = '/' + SENTINEL + '/';
    const translate = (url: string): string | null => {
        if (typeof url !== 'string') return null;
        const i = url.indexOf(marker);
        if (i === -1) return null;
        return assetBlobUrls.get(url.slice(i + marker.length)) || null;
    };

    let imgSrcDesc: PropertyDescriptor | undefined;
    let origImgSetAttr: HTMLImageElement['setAttribute'] | undefined;
    let styleObserver: MutationObserver | null = null;

    const installTranslators = (): void => {
        const proto = HTMLImageElement.prototype;

        // src property - React usually assigns through this.
        imgSrcDesc = Object.getOwnPropertyDescriptor(proto, 'src');
        if (imgSrcDesc && imgSrcDesc.get && imgSrcDesc.set) {
            const { get, set } = imgSrcDesc;
            Object.defineProperty(proto, 'src', {
                configurable: true,
                enumerable: imgSrcDesc.enumerable,
                get(this: HTMLImageElement) {
                    return get.call(this);
                },
                set(this: HTMLImageElement, value: string) {
                    set.call(this, translate(value) || value);
                },
            });
        }

        // setAttribute('src', ...) - the other way src can arrive.
        origImgSetAttr = proto.setAttribute;
        const orig = origImgSetAttr;
        proto.setAttribute = function (this: HTMLImageElement, name: string, value: string) {
            if (name === 'src') {
                return orig.call(this, name, translate(value) || value);
            }
            return orig.call(this, name, value);
        };
    };

    const removeTranslators = (): void => {
        const proto = HTMLImageElement.prototype;
        if (imgSrcDesc) {
            Object.defineProperty(proto, 'src', imgSrcDesc);
            imgSrcDesc = undefined;
        }
        if (origImgSetAttr) {
            proto.setAttribute = origImgSetAttr;
            origImgSetAttr = undefined;
        }
    };

    // Card variant paints its background via inline style; rewrite those in the
    // mounted subtree. Runs before paint (microtask), so the fetch uses the blob.
    const rewriteBackgrounds = (root: HTMLElement): void => {
        const fix = (el: HTMLElement) => {
            const bg = el.style && el.style.backgroundImage;
            if (bg && bg.indexOf(marker) !== -1) {
                const next = bg.replace(/url\((['"]?)([^'")]+)\1\)/g, (whole, _q, u) => {
                    const t = translate(u);
                    return t ? 'url("' + t + '")' : whole;
                });
                if (next !== bg) el.style.backgroundImage = next;
            }
        };
        fix(root);
        root.querySelectorAll<HTMLElement>('*').forEach(fix);
    };

    const observeBackgrounds = (section: HTMLElement): void => {
        rewriteBackgrounds(section);
        styleObserver = new MutationObserver((records) => {
            for (const rec of records) {
                if (rec.type === 'attributes' && rec.target instanceof HTMLElement) {
                    rewriteBackgrounds(rec.target);
                } else {
                    rec.addedNodes.forEach((n) => {
                        if (n instanceof HTMLElement) rewriteBackgrounds(n);
                    });
                }
            }
        });
        styleObserver.observe(section, {
            attributes: true,
            attributeFilter: ['style'],
            childList: true,
            subtree: true,
        });
    };

    // ── Bundle loading ─────────────────────────────────────────────────────
    let bundleScriptUrl: string | null = null;
    let styleEl: HTMLStyleElement | null = null;
    let sectionStyleEl: HTMLStyleElement | null = null;
    let loaded = false;

    const ensureSectionStyle = (): void => {
        if (document.getElementById(STYLE_ID)) return;
        sectionStyleEl = document.createElement('style');
        sectionStyleEl.id = STYLE_ID;
        sectionStyleEl.textContent = [
            '.' + SECTION_CLASS + ' { width: 100%; }',
            '.' + SECTION_CLASS + ' .egtd-qa-jpw-title {',
            '  display: flex; align-items: center; gap: 8px;',
            '  padding: 8px 12px; font: 600 13px/1.4 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;',
            '  color: #e8e8ea; background: #1c1c22; border-radius: 6px 6px 0 0;',
            '}',
            '.' + SECTION_CLASS + ' .egtd-qa-jpw-title .egtd-qa-jpw-tag {',
            '  padding: 2px 7px; border-radius: 10px; background: #2b2b34; color: #b9b9c4; font-weight: 400; font-size: 11px;',
            '}',
            '.' + SECTION_CLASS + ' .egtd-qa-jpw-row {',
            '  display: flex; align-items: flex-start; flex-wrap: wrap; gap: 16px; padding: 12px;',
            '}',
            '.' + SECTION_CLASS + ' .egtd-qa-jpw-item { flex: 0 0 auto; }',
        ].join('\n');
        document.head.appendChild(sectionStyleEl);
    };

    const load = (): Promise<void> => {
        if (loaded) return Promise.resolve();

        installTranslators();

        // The bundle's own CSS, inlined (no url() refs to rewrite).
        if (bundleCssText) {
            styleEl = document.createElement('style');
            styleEl.setAttribute('data-egtd-jpw-css', constants.version);
            styleEl.textContent = bundleCssText;
            document.head.appendChild(styleEl);
        }

        return new Promise<void>((resolve, reject) => {
            if (typeof g.EGTD_JackpotWidget === 'function') {
                loaded = true;
                resolve();
                return;
            }
            bundleScriptUrl = URL.createObjectURL(new Blob([bundleJsText], { type: 'text/javascript' }));
            createdUrls.push(bundleScriptUrl);
            const script = document.createElement('script');
            script.src = bundleScriptUrl;
            script.onload = () => {
                if (typeof g.EGTD_JackpotWidget !== 'function') {
                    reject(new Error('Loaded the bundle from the zip but it registered no widget factory.'));
                    return;
                }
                loaded = true;
                resolve();
            };
            script.onerror = () => reject(new Error('Failed to run the widget bundle from the zip.'));
            document.head.appendChild(script);
        });
    };

    // ── Mock stats (offline) ───────────────────────────────────────────────
    // Mirrors qa-inject.js: canned stats served in-page, values climbing each
    // poll so interpolation animates. Suffix match so it never touches the
    // lobby's own /api/jackpot/stats/internal.
    let nativeFetch: typeof fetch | null = null;
    let mockOn = false;
    let poll = 0;
    let mockCurrency = 'EUR';

    const buildStats = () => {
        const BASE: Record<number, number> = { 1: 75000000, 2: 12500000, 3: 500000, 4: 100000 };
        poll += 1;
        return {
            jackpotInstancesStats: {
                instanceStats: constants.jackpots.map((name, jpIndex) => ({
                    instanceName: name,
                    supportedCurrencies: [mockCurrency],
                    baseDenomination: 1,
                    baseDenominationCurrencyMap: { [mockCurrency]: 1 },
                    levelStats: [1, 2, 3, 4].map((levelId) => {
                        const value = BASE[levelId] + poll * (5000 - levelId * 900) + jpIndex * 137;
                        return {
                            levelId,
                            totalWins: 10 + levelId,
                            lastWinDate: '2026-01-15T10:00:00.000Z',
                            maxWinDate: '2025-11-02T18:30:00.000Z',
                            currentValue: [{ key: mockCurrency, value }],
                            lastWinValue: [{ key: mockCurrency, value: Math.round(value * 0.8) }],
                            maxWinValue: [{ key: mockCurrency, value: Math.round(value * 1.4) }],
                        };
                    }),
                })),
            },
        };
    };

    const isWidgetStatsUrl = (url: string): boolean => {
        try {
            const pathname = new URL(url, window.location.href).pathname;
            return pathname === STATS_PATH || pathname.slice(-STATS_PATH.length) === STATS_PATH;
        } catch {
            return false;
        }
    };

    const setMock = (on: boolean): void => {
        mockOn = Boolean(on);
        if (mockOn && !nativeFetch) {
            // Captured in a const so the wrapper closes over a non-null fetch.
            const native = g.fetch.bind(window);
            nativeFetch = native;
            g.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
                const url = typeof input === 'string' ? input : (input as Request).url || String(input);
                if (!mockOn || !isWidgetStatsUrl(url)) {
                    return native(input, init);
                }
                return Promise.resolve(
                    new Response(JSON.stringify(buildStats()), {
                        status: 200,
                        headers: { 'Content-Type': 'application/json' },
                    }),
                );
            }) as typeof fetch;
        }
    };

    // ── Mounting ───────────────────────────────────────────────────────────
    let section: HTMLElement | null = null;
    let mountCount = 0;

    const mount = (options: ZipMountOptions): HTMLElement => {
        // Captured so the per-jackpot loop below closes over a known-function
        // reference rather than re-reading the nullable global.
        const factory = g.EGTD_JackpotWidget;
        if (!loaded || typeof factory !== 'function') {
            throw new Error('mount() called before the zip bundle finished loading.');
        }

        const opts = options || {};
        const jackpots = (opts.jackpots && opts.jackpots.length ? opts.jackpots : constants.jackpots).filter(
            (name) => constants.jackpots.indexOf(name) !== -1,
        );
        const type = constants.types.indexOf(opts.type || '') !== -1 ? (opts.type as string) : 'standard';
        const currency = (opts.currency || 'EUR').toUpperCase();
        const units = typeof opts.units === 'number' ? opts.units : 100;
        const language = (opts.language || 'EN').toUpperCase();

        unmount();
        ensureSectionStyle();

        mockCurrency = currency;
        setMock(Boolean(opts.mock));

        // assetsHost points at the sentinel path; the translators above turn
        // those URLs into in-memory blobs. assetsLocation stays the default so
        // the paths inside match the zip layout.
        g.EGTD_JackpotWidgetConfig = {
            apiHost: opts.apiHost,
            currency,
            units,
            language,
            assetsHost: window.location.origin + '/' + SENTINEL,
            assetsLocation: ASSETS_LOCATION,
        };

        section = document.createElement('div');
        section.className = SECTION_CLASS + (opts.className ? ' ' + opts.className : '');
        section.setAttribute('data-egtd-qa-widgets', constants.version);

        if (opts.title !== '') {
            const title = document.createElement('div');
            title.className = 'egtd-qa-jpw-title';
            const label = document.createElement('span');
            label.textContent = opts.title || 'Jackpot widgets';
            title.appendChild(label);
            ['v' + constants.version, type, currency + ' /' + units, language, opts.mock ? 'MOCK DATA' : 'LIVE'].forEach(
                (text) => {
                    const tag = document.createElement('span');
                    tag.className = 'egtd-qa-jpw-tag';
                    tag.textContent = text;
                    title.appendChild(tag);
                },
            );
            section.appendChild(title);
        }

        const row = document.createElement('div');
        row.className = 'egtd-qa-jpw-row';
        section.appendChild(row);

        mountCount += 1;
        jackpots.forEach((jackpot, index) => {
            const item = document.createElement('div');
            item.className = 'egtd-qa-jpw-item';
            item.id = 'egtd-qa-jpw-' + mountCount + '-' + index;
            row.appendChild(item);
            factory({ el: item, jackpot, type });
        });

        observeBackgrounds(section);
        return section;
    };

    function unmount(): void {
        if (styleObserver) {
            styleObserver.disconnect();
            styleObserver = null;
        }
        if (section && section.parentNode) {
            section.parentNode.removeChild(section);
        }
        section = null;
    }

    const isMounted = (): boolean => Boolean(section && section.isConnected);

    /**
     * Full teardown: the bundle has no dispose API, so its React roots survive
     * (same caveat as the remote path), but we release what we own - the DOM
     * hooks, the injected styles and every blob URL - so the page is not left
     * leaking megabytes of decoded assets after Remove.
     */
    const destroy = (): void => {
        unmount();
        removeTranslators();
        if (styleEl && styleEl.parentNode) styleEl.parentNode.removeChild(styleEl);
        if (sectionStyleEl && sectionStyleEl.parentNode) sectionStyleEl.parentNode.removeChild(sectionStyleEl);
        createdUrls.forEach((u) => URL.revokeObjectURL(u));
        createdUrls.length = 0;
        assetBlobUrls.clear();
    };

    return {
        version: constants.version,
        base: '(zip)',
        JACKPOTS: constants.jackpots,
        TYPES: constants.types,
        LANGUAGES: constants.languages,
        load,
        mount: mount as unknown as WidgetInjector['mount'],
        unmount,
        isMounted,
        setMock,
        destroy,
    };
}
